import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowUp } from 'lucide-react';
import axios from 'axios';
import { heroSuggestions, footerLinks } from '../../mock';
import Logo from './Logo';
import { useAuth } from '../../lib/AuthContext';
import { supabase, PENDING_PROMPT_KEY } from '../../lib/supabase';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const deriveName = (p) => {
  const cleaned = (p || '').replace(/\s+/g, ' ').trim();
  return cleaned.length > 60 ? cleaned.slice(0, 57) + '\u2026' : cleaned || 'Untitled project';
};

// Same server-side plan gate as `Hero.jsx`. Fails open on network errors —
// the `/generate*` endpoints re-enforce the cap.
const preflightNewProject = async () => {
  try {
    const { data: sessionData } = await supabase.auth.getSession();
    const token = sessionData?.session?.access_token;
    if (!token) return null;
    const { data } = await axios.get(`${API}/projects-preflight`, {
      headers: { Authorization: `Bearer ${token}` },
      timeout: 8000,
    });
    if (data && data.allowed === false) {
      return data.message || 'You’ve hit your saved-project limit.';
    }
    return null;
  } catch (_) {
    return null;
  }
};

export const FinalCTA = () => {
  const [value, setValue] = useState('');
  const [phraseIdx, setPhraseIdx] = useState(0);
  const [display, setDisplay] = useState('');
  const [status, setStatus] = useState('idle'); // idle | loading | error
  const [errMsg, setErrMsg] = useState('');
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (value) return;
    const target = heroSuggestions[phraseIdx];
    let i = 0;
    const timer = setInterval(() => {
      i++;
      setDisplay(target.slice(0, i));
      if (i >= target.length) {
        clearInterval(timer);
        setTimeout(() => {
          setPhraseIdx((p) => (p + 1) % heroSuggestions.length);
          setDisplay('');
        }, 1800);
      }
    }, 45);
    return () => clearInterval(timer);
  }, [phraseIdx, value]);

  const handleBuild = async () => {
    const prompt = value.trim();
    if (!prompt) {
      setStatus('error');
      setErrMsg('Type what you want built first.');
      return;
    }
    if (!user) {
      sessionStorage.setItem(PENDING_PROMPT_KEY, prompt);
      navigate('/signup?next=/projects');
      return;
    }
    setStatus('loading');
    setErrMsg('');
    const capMsg = await preflightNewProject();
    if (capMsg) {
      setStatus('error');
      setErrMsg(capMsg);
      return;
    }
    const { data: inserted, error } = await supabase
      .from('user_projects')
      .insert({ user_id: user.id, name: deriveName(prompt), prompt })
      .select('id')
      .single();
    if (error) {
      setStatus('error');
      setErrMsg(error.message || 'Could not save your project.');
      return;
    }
    navigate(`/projects/${inserted.id}`, { state: { autoGenerate: true } });
  };

  return (
    <section
      id="get-started"
      className="relative py-32 lg:py-40 overflow-hidden"
      style={{ background: 'linear-gradient(to bottom, #FBFAF7, #F1EFFB 55%, #FBFAF7)' }}
    >
      <div className="absolute inset-0 pointer-events-none">
        <div
          className="absolute top-1/2 -translate-y-1/2 left-1/2 -translate-x-1/2 w-[900px] h-[900px] rounded-full blur-3xl"
          style={{ background: 'radial-gradient(circle, rgba(79,70,229,0.18), rgba(245,158,11,0.08) 55%, transparent 75%)' }}
        />
      </div>

      <div className="relative max-w-[1000px] mx-auto px-6 text-center">
        <p className="text-[13px] uppercase tracking-widest mb-6" style={{ color: 'var(--yb-muted)' }}>Your turn</p>
        <h2 className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-[-0.02em]" style={{ color: 'var(--yb-ink)' }}>
          Your next customer<br />
          <span className="italic">is one page away.</span>
        </h2>
        <p className="mt-6 text-[16px] sm:text-[17px] max-w-xl mx-auto" style={{ color: 'var(--yb-muted)' }}>
          Describe your trade business, and YouBuild builds a website that captures quote requests.
        </p>

        <div className="mt-12 max-w-[680px] mx-auto">
          <div className="relative bg-white rounded-2xl border p-4 pt-5 shadow-[0_20px_60px_-20px_rgba(23,21,59,0.25)]" style={{ borderColor: 'var(--yb-border)' }}>
            <div className="relative">
              <textarea
                value={value}
                onChange={(e) => setValue(e.target.value)}
                rows={2}
                aria-label="Describe your trade business"
                data-testid="footer-cta-textarea"
                className="w-full resize-none bg-transparent text-[16px] outline-none px-2 text-left"
                style={{ color: 'var(--yb-ink)' }}
              />
              {!value && (
                <div className="pointer-events-none absolute top-0 left-2 text-[16px] text-left" style={{ color: '#9CA3AF' }}>
                  Ask YouBuild to create {display}
                  <span className="inline-block w-[2px] h-[18px] ml-0.5 align-middle animate-pulse" style={{ backgroundColor: '#9CA3AF' }} />
                </div>
              )}
            </div>
          </div>

          <div className="mt-6 flex flex-col items-center gap-3">
            <button
              type="button"
              onClick={handleBuild}
              disabled={status === 'loading'}
              data-testid="footer-cta-build-btn"
              className="yb-btn-primary inline-flex items-center gap-2 px-6 py-3 rounded-full text-[14px] font-semibold disabled:opacity-70"
            >
              {status === 'loading' ? 'Saving…' : (<>Build my website <ArrowUp size={15} /></>)}
            </button>
            {status === 'error' && (
              <p className="text-[13px]" style={{ color: '#B4530A' }}>{errMsg}</p>
            )}
            {status === 'idle' && !user && (
              <p className="text-[12.5px]" style={{ color: 'var(--yb-muted)' }}>
                Free to start, no credit card. Publish when you're ready.
              </p>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export const Footer = () => {
  const renderLink = (item) => {
    const linkStyle = { color: '#B8BBC7' };
    const onEnter = (e) => (e.currentTarget.style.color = '#FFFFFF');
    const onLeave = (e) => (e.currentTarget.style.color = '#B8BBC7');
    const cls = 'text-[13px] transition-colors';
    if (item.type === 'route') {
      return (
        <Link
          to={item.href}
          className={cls}
          style={linkStyle}
          onMouseEnter={onEnter}
          onMouseLeave={onLeave}
          data-testid={`footer-link-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
        >
          {item.label}
        </Link>
      );
    }
    return (
      <a
        href={item.href}
        className={cls}
        style={linkStyle}
        onMouseEnter={onEnter}
        onMouseLeave={onLeave}
        data-testid={`footer-link-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
      >
        {item.label}
      </a>
    );
  };

  return (
    <footer className="py-20 lg:py-24" style={{ backgroundColor: 'var(--yb-ink)', color: '#D1D5DB' }} data-testid="site-footer">
      <div className="max-w-[1200px] mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-10">
          <div className="col-span-2">
            <div style={{ color: '#FFFFFF' }}>
              <Logo />
            </div>
            <p className="mt-4 text-[13.5px] max-w-xs leading-relaxed" style={{ color: '#B8BBC7' }} data-testid="footer-tagline">
              A website builder for Australian tradies and local businesses, that actually brings in jobs. Describe your business, publish live in minutes.
            </p>
            <p className="mt-4 text-[12px]" style={{ color: '#8A8FA0' }}>you-build.app</p>
          </div>
          {Object.entries(footerLinks).map(([title, items]) => (
            <div key={title}>
              <p className="text-[13px] font-medium mb-4" style={{ color: '#FFFFFF' }}>{title}</p>
              <ul className="space-y-2.5">
                {items.map((item) => (
                  <li key={item.label}>{renderLink(item)}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="mt-16 pt-8 border-t" style={{ borderColor: 'rgba(255,255,255,0.08)' }}>
          <p className="text-[12.5px]" style={{ color: '#8A8FA0' }}>
            © {new Date().getFullYear()} YouBuild. Built in Australia for people who work with their hands.
          </p>
        </div>
      </div>
    </footer>
  );
};
